import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { PartnerSelector } from './PartnerSelector';
import { processedTransactionApi } from '../lib/api/ProcessedTransactionApi';
import { payerApi } from '../lib/api/payerApi';
import { paymentMethodApi } from '../lib/api/paymentMethodApi';
import { merchantApi } from '../lib/api/merchantApi';
import type { PartnerDto } from '../lib/api/types';

// Helper to map payment method types to a friendly category label
const CARD_TYPES = [
  'Visa',
  'MasterCard',
  'AMEX',
  'Discover',
  'DinersClub',
  'JCB',
  'Maestro',
  'CarteBancaire',
] as const;
const BANK_TYPES = ['Checking', 'Savings'] as const;
const TRANSFER_TYPES = ['ProPayToProPay', 'SEPA'] as const;

const getPaymentMethodKindLabel = (type: string) => {
  if ((CARD_TYPES as readonly string[]).includes(type)) return 'Credit Card';
  if ((BANK_TYPES as readonly string[]).includes(type)) return 'Bank Account';
  if ((TRANSFER_TYPES as readonly string[]).includes(type)) return 'Transfer';
  return type;
};

const formSchema = z.object({
  payerId: z.string().min(1, 'Payer is required'),
  payerAccountId: z.string().min(1, 'Payer Account ID is required'),
  PaymentMethodID: z.string().min(1, 'Payment Method is required'),
  amount: z.number().min(0.01, 'Amount must be greater than 0'),
  currencyCode: z.string().min(1, 'Currency code is required'),
  merchantProfileId: z.number().min(1, 'Merchant Profile is required'),
  originatingAccountId: z.number()
    .min(1, 'Originating Account ID is required')
    .max(999999999, 'Originating Account ID cannot exceed 9 digits'),
  receivingAccountId: z.number()
    .min(1, 'Receiving Account ID is required')
    .max(999999999, 'Receiving Account ID cannot exceed 9 digits'),
  invoice: z.string().optional(),
  comment1: z.string().optional(),
  comment2: z.string().optional(),
  isDebtRepayment: z.boolean().optional(),
  // Partner authentication fields
  partnerId: z.string().min(1, 'Partner selection is required'),
  authToken: z.string().min(1, 'Auth token is required'),
});

type FormData = z.infer<typeof formSchema>;

interface CreateProcessedTransactionFormProps {
  onSuccess?: () => void;
}

export const CreateProcessedTransactionForm: React.FC<CreateProcessedTransactionFormProps> = ({
  onSuccess,
}) => {
  const [selectedPayerId, setSelectedPayerId] = useState<string>('');
  const [selectedPartner, setSelectedPartner] = useState<PartnerDto | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      currencyCode: 'USD',
      isDebtRepayment: false,
      partnerId: '',
      authToken: '',
    },
  });

  // Watch for payer selection changes
  const watchedPayerId = watch('payerId');

  // Fetch payers
  const { data: payersData, isLoading: isLoadingPayers } = useQuery({
    queryKey: ['payers'],
    queryFn: () => payerApi.getAllPayers(),
  });

  // Fetch merchant profiles
  const { data: merchantProfilesData, isLoading: isLoadingMerchantProfiles } = useQuery({
    queryKey: ['merchantProfiles'],
    queryFn: () => merchantApi.getAllMerchantProfiles(),
  });

  // Fetch payment methods for selected payer
  const { data: paymentMethodsData, isLoading: isLoadingPaymentMethods } = useQuery({
    queryKey: ['paymentMethods', selectedPayerId],
    queryFn: () => paymentMethodApi.adminListPaymentMethodsByPayerId(selectedPayerId),
    enabled: !!selectedPayerId,
  });

  // Update selected payer when form value changes
  useEffect(() => {
    if (watchedPayerId && watchedPayerId !== selectedPayerId) {
      setSelectedPayerId(watchedPayerId);
      // Reset payment method selection when payer changes
      setValue('PaymentMethodID', '');
    }
  }, [watchedPayerId, selectedPayerId, setValue]);

  // Update payerAccountId when payer is selected
  useEffect(() => {
    if (watchedPayerId && payersData?.Payers) {
      const selectedPayer = payersData.Payers.find(payer => payer.PayerAccountId === watchedPayerId);
      if (selectedPayer) {
        setValue('payerAccountId', selectedPayer.PayerAccountId);
      }
    }
  }, [watchedPayerId, payersData, setValue]);

  // Handle partner selection
  const handlePartnerSelect = (partner: PartnerDto | null) => {
    setSelectedPartner(partner);
    setValue('partnerId', partner?.partnerId || '');
  };

  const createMutation = useMutation({
    mutationFn: ({ payerId, request, billerId, authToken }: {
      payerId: string;
      request: any;
      billerId: string;
      authToken: string;
    }) =>
      processedTransactionApi.createProcessedTransaction(payerId, request, billerId, authToken),
    onSuccess: () => {
      toast.success('Transaction created successfully!');
      reset();
      setSelectedPartner(null);
      onSuccess?.();
    },
    onError: (error: any) => {
      toast.error(`Failed to create transaction: ${error.message}`);
    },
  });

  const onSubmit = (data: FormData) => {
    if (!selectedPartner) {
      toast.error('Please select a partner');
      return;
    }

    if (!selectedPartner.billerId) {
      toast.error('Selected partner does not have a biller ID configured');
      return;
    }

    const requestData = {
      PayerAccountId: data.payerAccountId,
      PaymentMethodID: data.PaymentMethodID,
      Amount: data.amount,
      CurrencyCode: data.currencyCode,
      MerchantProfileId: data.merchantProfileId,
      Invoice: data.invoice,
      Comment1: data.comment1,
      Comment2: data.comment2,
      IsDebtRepayment: !!data.isDebtRepayment,
      IsRecurringPayment: false,
    };

    createMutation.mutate({
      payerId: data.payerId,
      request: requestData,
      billerId: selectedPartner.billerId,
      authToken: data.authToken
    });
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Create Processed Transaction</CardTitle>
        <CardDescription>
          Create a new Processed transaction with payer, payment method, and merchant profile details.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Payer Selection */}
          <div className="space-y-2">
            <Label htmlFor="payerId">Payer *</Label>
            <select
              id="payerId"
              {...register('payerId')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoadingPayers}
            >
              <option value="">
                {isLoadingPayers ? 'Loading payers...' : 'Select a payer'}
              </option>
              {payersData?.Payers?.map((payer) => (
                <option key={payer.PayerAccountId} value={payer.PayerAccountId}>
                  {payer.Name} ({payer.EmailAddress || payer.PayerAccountId})
                </option>
              ))}
            </select>
            {errors.payerId && (
              <p className="text-sm text-red-600">{errors.payerId.message}</p>
            )}
          </div>

          {/* Payment Method Selection */}
          <div className="space-y-2">
            <Label htmlFor="paymentMethodID">Payment Method *</Label>
            <select
              id="PaymentMethodID"
              {...register('PaymentMethodID')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={!selectedPayerId || isLoadingPaymentMethods}
            >
              <option value="">
                {!selectedPayerId
                  ? 'Select a payer first'
                  : isLoadingPaymentMethods
                    ? 'Loading payment methods...'
                    : 'Select a payment method'
                }
              </option>
              {paymentMethodsData?.PaymentMethods?.map((paymentMethod) => {
                const isCard = (CARD_TYPES as readonly string[]).includes(paymentMethod.PaymentMethodType);
                const displayType = isCard
                  ? `${paymentMethod.PaymentMethodType}`
                  : getPaymentMethodKindLabel(paymentMethod.PaymentMethodType);

                return (
                  <option key={paymentMethod.PaymentMethodId} value={paymentMethod.PaymentMethodId}>
                    {paymentMethod.AccountName} ({displayType})
                  </option>
                );
              })}
            </select>
            {errors.PaymentMethodID && (
              <p className="text-sm text-red-600">{errors.PaymentMethodID.message}</p>
            )}
          </div>

          {/* Merchant Profile Selection */}
          <div className="space-y-2">
            <Label htmlFor="merchantProfileId">Merchant Profile *</Label>
            <select
              id="merchantProfileId"
              {...register('merchantProfileId', { valueAsNumber: true })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoadingMerchantProfiles}
            >
              <option value="">
                {isLoadingMerchantProfiles ? 'Loading merchant profiles...' : 'Select a merchant profile'}
              </option>
              {merchantProfilesData?.merchantProfiles?.map((profile) => (
                <option key={profile.profileId} value={profile.profileId}>
                  {profile.profileName} ({profile.paymentProcessor})
                </option>
              ))}
            </select>
            {errors.merchantProfileId && (
              <p className="text-sm text-red-600">{errors.merchantProfileId.message}</p>
            )}
          </div>

          {/* Partner Selection */}
          <div className="space-y-2">
            <PartnerSelector
              selectedPartnerId={selectedPartner?.partnerId || ''}
              onPartnerSelect={handlePartnerSelect}
              label="Partner *"
              placeholder="Select a partner"
              required
              error={errors.partnerId?.message}
            />
          </div>

          {/* Auth Token */}
          <div className="space-y-2">
            <Label htmlFor="authToken">Auth Token *</Label>
            <Input
              id="authToken"
              type="text"
              placeholder="Enter authentication token"
              {...register('authToken')}
            />
            {errors.authToken && (
              <p className="text-sm text-red-600">{errors.authToken.message}</p>
            )}
          </div>

          {/* Hidden Payer Account ID field (auto-populated) */}
          <input type="hidden" {...register('payerAccountId')} />

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">Amount *</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              placeholder="Enter amount"
              {...register('amount', { valueAsNumber: true })}
            />
            {errors.amount && (
              <p className="text-sm text-red-600">{errors.amount.message}</p>
            )}
          </div>

          {/* Currency Code */}
          <div className="space-y-2">
            <Label htmlFor="currencyCode">Currency Code *</Label>
            <Input
              id="currencyCode"
              placeholder="USD"
              {...register('currencyCode')}
            />
            {errors.currencyCode && (
              <p className="text-sm text-red-600">{errors.currencyCode.message}</p>
            )}
          </div>

          {/* Originating Account ID */}
          <div className="space-y-2">
            <Label htmlFor="originatingAccountId">Originating Account ID *</Label>
            <Input
              id="originatingAccountId"
              type="number"
              placeholder="Enter originating account ID"
              {...register('originatingAccountId', { valueAsNumber: true })}
            />
            {errors.originatingAccountId && (
              <p className="text-sm text-red-600">{errors.originatingAccountId.message}</p>
            )}
          </div>

          {/* Receiving Account ID */}
          <div className="space-y-2">
            <Label htmlFor="receivingAccountId">Receiving Account ID *</Label>
            <Input
              id="receivingAccountId"
              type="number"
              placeholder="Enter receiving account ID"
              {...register('receivingAccountId', { valueAsNumber: true })}
            />
            {errors.receivingAccountId && (
              <p className="text-sm text-red-600">{errors.receivingAccountId.message}</p>
            )}
          </div>

          {/* Optional Fields */}
          <div className="space-y-2">
            <Label htmlFor="invoice">Invoice (Optional)</Label>
            <Input
              id="invoice"
              placeholder="Enter invoice number"
              {...register('invoice')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="comment1">Comment 1 (Optional)</Label>
            <Input
              id="comment1"
              placeholder="Enter comment"
              {...register('comment1')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="comment2">Comment 2 (Optional)</Label>
            <Input
              id="comment2"
              placeholder="Enter comment"
              {...register('comment2')}
            />
          </div>

          {/* Debt Repayment Checkbox */}
          <div className="flex items-center space-x-2">
            <input
              id="isDebtRepayment"
              type="checkbox"
              {...register('isDebtRepayment')}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <Label htmlFor="isDebtRepayment">Is Debt Repayment</Label>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Transaction'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
